export const DIMENSIONS = [
  {
    num: 1,
    key: 'd1',
    label: 'Assess & Bound Risks',
    shortLabel: 'Risk Bounding',
    color: '#6366F1',
    tailwind: 'indigo',
  },
  {
    num: 2,
    key: 'd2',
    label: 'Human Accountability',
    shortLabel: 'Accountability',
    color: '#0EA5E9',
    tailwind: 'sky',
  },
  {
    num: 3,
    key: 'd3',
    label: 'Technical Controls',
    shortLabel: 'Tech Controls',
    color: '#10B981',
    tailwind: 'emerald',
  },
  {
    num: 4,
    key: 'd4',
    label: 'End-User Responsibility',
    shortLabel: 'User Responsibility',
    color: '#F59E0B',
    tailwind: 'amber',
  },
];

export const QUESTIONS = {
  1: [
    {
      id: 'd1q1',
      text: 'Autonomy classification: Has each deployed agent been formally classified by its level of autonomy?',
      frameworks: ['APS5', 'DISR_SAFE', 'ISO42001'],
    },
    {
      id: 'd1q2',
      text: 'Sensitive data access: Has a data-sensitivity assessment been completed for each agent?',
      frameworks: ['APS4', 'DISR_SECURE', 'APP11'],
    },
    {
      id: 'd1q3',
      text: 'Reversibility: Are agent actions categorised by reversibility, with extra controls on irreversible ones?',
      frameworks: ['APS5', 'DISR_SAFE', 'DISR_RELIABLE'],
    },
    {
      id: 'd1q4',
      text: 'Tool register: Is there a documented register of every tool/API each agent is authorised to invoke?',
      frameworks: ['APS5', 'DISR_SECURE', 'ISO42001'],
    },
    {
      id: 'd1q5',
      text: 'Sandbox environments: Are agents tested in sandboxed environments before production deployment?',
      frameworks: ['APS5', 'DISR_SAFE', 'DISR_RELIABLE'],
    },
    {
      id: 'd1q6',
      text: 'Identity & permissions: Does each agent have a distinct identity with least-privilege permissions?',
      frameworks: ['APS4', 'DISR_SECURE', 'ISO42001'],
    },
    {
      id: 'd1q7',
      text: 'Privacy Impact Assessment: Has a PIA been completed for each agent handling personal information, per Australian Privacy Act APP requirements?',
      frameworks: ['APS4', 'APP1', 'APP3', 'APP11'],
    },
    {
      id: 'd1q8',
      text: 'Bias & fairness assessment: Has an algorithmic bias assessment been conducted in line with APS AI Ethics Principle 3 (Fairness) and relevant anti-discrimination legislation?',
      frameworks: ['APS3', 'DISR_FAIR'],
    },
  ],
  2: [
    {
      id: 'd2q1',
      text: 'Role definition: Are developer/deployer/operator/end-user roles formally defined for each agent?',
      frameworks: ['APS8', 'DISR_ACCOUNTABLE', 'ISO42001'],
    },
    {
      id: 'd2q2',
      text: 'Human-in-the-loop: Are explicit human approval steps built into high-autonomy agent workflows?',
      frameworks: ['APS2', 'APS8', 'DISR_ACCOUNTABLE'],
    },
    {
      id: 'd2q3',
      text: 'High-stakes checkpoints: Is there a documented list of high-stakes action categories with checkpoint procedures?',
      frameworks: ['APS5', 'APS8', 'DISR_SAFE', 'DISR_ACCOUNTABLE'],
    },
    {
      id: 'd2q4',
      text: 'Automation bias training: Are staff trained to critically evaluate agent outputs?',
      frameworks: ['APS2', 'APS8', 'DISR_ACCOUNTABLE'],
    },
    {
      id: 'd2q5',
      text: 'Override/intercept: Can operators pause or roll back agent actions without developer intervention?',
      frameworks: ['APS7', 'DISR_CONTESTABLE', 'DISR_ACCOUNTABLE'],
    },
    {
      id: 'd2q6',
      text: 'Contestability mechanism: Is there a documented process for individuals to contest AI-generated decisions, per APS AI Ethics Principle 7 (Contestability) and DISR Responsible AI requirements?',
      frameworks: ['APS7', 'DISR_CONTESTABLE', 'APP12'],
    },
    {
      id: 'd2q7',
      text: 'APS mandatory assessment: For public sector deployments, has an APS-compliant AI impact assessment been completed ahead of the 2026 mandatory assessment requirement?',
      frameworks: ['APS8', 'DISR_ACCOUNTABLE', 'ISO42001'],
    },
  ],
  3: [
    {
      id: 'd3q1',
      text: 'Tool guardrails: Are programmatic guardrails enforcing tool-call policy outside the model?',
      frameworks: ['APS5', 'DISR_SAFE', 'DISR_RELIABLE'],
    },
    {
      id: 'd3q2',
      text: 'Plan validation: Is there a validation step checking multi-step plans against constraints before execution?',
      frameworks: ['APS5', 'DISR_RELIABLE', 'ISO42001'],
    },
    {
      id: 'd3q3',
      text: 'Least-privilege reviews: Are agent permissions reviewed and right-sized at least quarterly?',
      frameworks: ['APS4', 'DISR_SECURE', 'APP11'],
    },
    {
      id: 'd3q4',
      text: 'Output quality testing: Is there a benchmark test suite measuring agent output accuracy per deployment?',
      frameworks: ['APS5', 'DISR_RELIABLE', 'ISO42001'],
    },
    {
      id: 'd3q5',
      text: 'Policy compliance testing: Are automated tests run to verify agents comply with safety/content policies?',
      frameworks: ['APS5', 'DISR_SAFE', 'DISR_RELIABLE'],
    },
    {
      id: 'd3q6',
      text: 'Gradual rollout + monitoring: Are agents rolled out incrementally with real-time anomaly alerting?',
      frameworks: ['APS5', 'DISR_SAFE', 'DISR_RELIABLE'],
    },
    {
      id: 'd3q7',
      text: 'Explainability: Can the system generate plain-language explanations of AI-influenced decisions, per APS AI Ethics Principle 6 (Transparency & Explainability) and DISR Explainable requirements?',
      frameworks: ['APS6', 'DISR_EXPLAINABLE', 'DISR_TRANSPARENT'],
    },
    {
      id: 'd3q8',
      text: 'Data sovereignty: Is personal data processed and stored within Australian jurisdiction, or are cross-border transfers covered by appropriate contractual safeguards under the Privacy Act?',
      frameworks: ['APS4', 'APP6', 'APP11'],
    },
  ],
  4: [
    {
      id: 'd4q1',
      text: 'User training: Have structured training programs been delivered to all end users?',
      frameworks: ['APS2', 'APS8', 'DISR_ACCOUNTABLE'],
    },
    {
      id: 'd4q2',
      text: 'Capability transparency: Are users clearly informed about what each agent can/cannot do?',
      frameworks: ['APS6', 'DISR_TRANSPARENT', 'APP1'],
    },
    {
      id: 'd4q3',
      text: 'Oversight procedures: Do users have documented procedures for querying or escalating agent outputs?',
      frameworks: ['APS7', 'DISR_CONTESTABLE', 'APP12'],
    },
    {
      id: 'd4q4',
      text: 'Over-reliance monitoring: Does the org monitor for over-reliance patterns and act on them?',
      frameworks: ['APS2', 'APS5', 'DISR_RELIABLE'],
    },
    {
      id: 'd4q5',
      text: 'Stakeholder education: Have senior stakeholders received governance-level briefings on agentic AI?',
      frameworks: ['APS8', 'DISR_ACCOUNTABLE', 'ISO42001'],
    },
    {
      id: 'd4q6',
      text: 'Vulnerable user protections: Are additional safeguards in place when AI systems interact with vulnerable populations (elderly, people with disability, First Nations communities), per APS AI Ethics Principles 1 & 2?',
      frameworks: ['APS1', 'APS2', 'APS3', 'DISR_FAIR'],
    },
    {
      id: 'd4q7',
      text: 'Environmental & societal wellbeing: Has the broader societal and environmental impact of AI deployments been assessed and documented, per APS AI Ethics Principle 1?',
      frameworks: ['APS1', 'DISR_SAFE'],
    },
  ],
};

export function getDimension(num) {
  return DIMENSIONS.find((d) => d.num === num);
}
